import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { ToastContainer } from "react-toastify";
import { compressFile, mobileCheck } from "src/helpers/mixins";
import Notify from "src/toast/Notify";
import DialogInfoGeneral from "./DialogInfoGeneral";
import { FaArrowDownLong } from "react-icons/fa6";
import APIService from "src/services/Api";
import { UseAuth } from "src/auth/AuthContext";
import { FaCheckCircle } from "react-icons/fa";

type ParamsUploadMF = {
  type: string;
  text?: string
  transferId: string
  newStatus?: string
  success(): void
  currentStatus: string
  setLoadingUpload(val:boolean):void
};
type IPreview = {
  type: string;
  url: string;
  size: number;
  name: string;
  canPreview?: boolean;
  
};

const DepositUploadManyFiles: React.FC<ParamsUploadMF> = ({ currentStatus,text,type,transferId,newStatus,success,setLoadingUpload}) => {
  const { user } = UseAuth()
  const [files, setFiles] = useState<File[]>([]);
  const [drag, setDrag] = useState(false)
  const [preview, setPreview] = useState<IPreview[]>([]);
  const [showAlertMaxFile, setShowAlertMaxFile] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false)
  const [localSuccess, setLocalSuccess] = useState<boolean>(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isMobile = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }
    try {
      return mobileCheck();
    } catch (error) {
      console.error("Error detecting mobile device", error);
      return false;
    }
  }, []);

  useEffect(() => {
    return () => {
      preview.forEach((item) => {
        if (item?.url) {
          URL.revokeObjectURL(item.url);
        }
      });
    };
  }, [preview]);

  const handleUpload = async() => {
    try{
      setLoading(true)
      setLoadingUpload(true)
      const data = new FormData() as any
      const fileKeys = ['fileOne','fileTwo','fileThree']
      fileKeys.forEach((key, index) => {
        const currentFile = files[index]
        if(currentFile){
          data.append(key, currentFile)
        }
      })
      data.append('responsable',user?.role || '')
      data.append('status','checking')
      data.append('transferId',transferId)
      if(newStatus!==currentStatus && newStatus!==undefined){
        data.append('newstatus',newStatus)
      }
      data.append('type',type || '')
      const res = await APIService.transferUploadManyFiles(data)

      if(res?.data?.status){
        setLocalSuccess(true)
        success()
      }else{
        Notify('No fue posible enviar los comprobantes de pago.')
      }
    }catch(e){
      console.log(e)
      Notify('Lo sentimos no es posible enviar los comprobantes de pago.')
    }finally {
      setLoading(false)
      setLoadingUpload(false)
    } 
  }


  const generatePreview = async (filesGot: File[]) => {
    setDrag(false)
    const normalizedFiles = Array.isArray(filesGot) ? filesGot : [];
    const spacesAvailable = files.length === 0 ? 3 : 3 - files.length;

    if (spacesAvailable <= 0) {
      setShowAlertMaxFile(true);
      return;
    }

    const candidates = normalizedFiles.slice(0, spacesAvailable);
    if (candidates.length === 0) {
      setShowAlertMaxFile(true);
      return;
    }

    const allowedMime = new Set([
      "image/jpeg",
      "image/jpg",
      "image/png",
    ]);
    const allowedExtensions = new Set([
      "jpeg",
      "jpg",
      "png",
    ]);

    const processedFiles: File[] = [];
    const processedPreview: IPreview[] = [];

    for (const file of candidates) {
      const rawType = file.type?.toLowerCase() || "";
      const extension = file.name?.split(".").pop()?.toLowerCase() || "";
      const isImageMime = rawType.startsWith("image/");
      const isAllowedMime = allowedMime.has(rawType);
      const isAllowedExtension = allowedExtensions.has(extension);

      if (!isImageMime && !isAllowedMime && !isAllowedExtension) {
        Notify(
          "Solo aceptamos imágenes (PNG, JPG, HEIC, HEIF o WEBP)",
          "error"
        );
        continue;
      }

      let fileToUse: File = file;
      try {
        const maybeCompressed = compressFile(file) as
          | File
          | Promise<File>
          | undefined;
        if (
          maybeCompressed &&
          typeof (maybeCompressed as { then?: unknown })?.then === "function"
        ) {
          fileToUse = await (maybeCompressed as Promise<File>);
        } else if (maybeCompressed instanceof File) {
          fileToUse = maybeCompressed;
        }
      } catch (error) {
        console.error("Error compressing file", error);
      }

      if (!fileToUse) {
        fileToUse = file;
      }

      const mimeType = fileToUse.type || rawType || "image/*";
      const unsupportedPreviewTypes = ["image/heic", "image/heif"];
      const canPreview = !unsupportedPreviewTypes.includes(
        mimeType.toLowerCase()
      );
      const objectUrl = canPreview ? URL.createObjectURL(fileToUse) : "";

      processedFiles.push(fileToUse);
      processedPreview.push({
        type: mimeType,
        url: objectUrl,
        size: fileToUse.size / 1024 ** 2,
        name: fileToUse.name || file.name,
        canPreview,
      });
    }

    if (processedFiles.length === 0) {
      return;
    }

    setFiles((prev) => [...prev, ...processedFiles]);
    setPreview((prev) => [...prev, ...processedPreview]);
  };
  const onDrop = (acceptedFiles: any) => {
    void generatePreview(acceptedFiles);
  };

  const removeFile = (i: number) => {
    setPreview((prev) => {
      const current = prev[i];
      if (current?.url) {
        URL.revokeObjectURL(current.url);
      }
      return prev.filter((_: any, key: number) => key !== i);
    });
    setFiles((prev) => prev.filter((_: File, key: number) => key !== i));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 3,
    onDragEnter: () => {setDrag(true)},
    onDragLeave: () => {setDrag(false)},
    noClick: isMobile,
    disabled: isMobile,
    accept: {
      "image/jpeg": [],
      "image/jpg": [],
      "image/png": [],
      "image/webp": [],
      "image/heic": [],
      "image/heif": [],
      "image/*": [],
    },
  });

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelection = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files
      ? Array.from(event.target.files).filter(
          (file): file is File => Boolean(file)
        )
      : [];
    if (selectedFiles.length > 0) {
      void generatePreview(selectedFiles);
    }

    event.target.value = "";
  };

  return (
    <div>
        <>
        {(files.length < 3 && !loading )&& 
          <>
            {isMobile ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/*"
                  multiple
                  className="hidden"
                  onChange={handleFileSelection}
                />
                <button
                  type="button"
                  onClick={handleOpenFilePicker}
                  className="w-full text-center mx-auto border-2 py-4 border-green-admin-light mb-2 mt-3 rounded-sm border-dashed flex flex-col justify-center items-center cursor-pointer bg-white"
                >
                  <div className="px-6 text-base py-2 opensans font-semibold">
                    Selecciona hasta 3 comprobantes de pago desde tu galería.
                  </div>
                  <button type="button" className="bg-yellow-400 text-black px-1 py-0.5 rounded-md">haz clic para seleccionarlos.</button>
                  <div className="mt-2 font-semibold text-sm">
                    Formatos permitidos: JPG, PNG.
                  </div>
                </button>
              </>
            ) : (
              <div {...getRootProps()}>
                  <input {...getInputProps()} />
                  <div className="w-full text-center mx-auto border-2 py-4 border-green-admin-light mb-2 mt-3 rounded-sm border-dashed flex flex-col justify-center items-center cursor-pointer">
            
                    <div className="px-6 text-base py-2 opensans font-semibold">
                      Arrastra y suelta los comprobantes de pago aquí o <button type="button" className="bg-yellow-400 text-black px-1 py-0.5 rounded-md">haz clic para seleccionarlos.</button>
                    </div>
                    {!drag ?
                    <>
                      <div className="mt-2 font-semibold text-sm">
                      Máximo 3 imagenes y formatos JPG, PNG.
                      </div>
                    </> 
                    :
                    <div className="font-medium text-center text-xl py-4">
                      Suelta el archivo 
                      <div className="flex items-center justify-center">
                        <FaArrowDownLong size={40} className="floating text-green-admin-light" />
                      </div>
                    </div>
                  }
                  </div>
              </div>
            )}
          </>
        }
          {files.length > 0 && (
              <div className="text-center flex flex-col justify-center gap-2 mt-2 mb-3">
                {!localSuccess && 
                  <div>
                      <button onClick={() => handleUpload()} className="bg-yellow-400 text-base font-medium mt-2 text-black px-4 py-2 rounded-xl mt-2">
                      {!loading ? 
                        <>
                          Enviar comprobante{files.length > 1 && "s"}
                        </>
                      :
                      <div className="text-center flex justify-center px-10 py-1">
                        <div className="loader-dots"></div>
                      </div>
                        }
                      </button>
                  </div>
                }
              </div>
            )}
          <div className="bg-gradient-card mb-2 flex flex-col items-center gap-2 py-4 rounded-md border border-stone-400">
            
            {preview?.length > 0 && 
              <div className="grid grid-cols-1 md:grid-cols-3 px-4 py-2 gap-4">
                {preview.map((p, i) => (
                  <div key={`${p.name}-${i}`} className="grid-col-1 relative">
                    {!loading && !localSuccess &&  
                      <div
                        onClick={() => removeFile(i)}
                        className="cursor-pointer rounded-full absolute -right-2 -top-3 bg-red-500 text-white w-6 h-6 flex items-center justify-center font-bold"
                      >
                        X
                      </div>
                    }
                    {(p.canPreview ?? true) && p.url ? (
                      <div
                        className="w-full h-20 rounded-md bg-image-complete border border-stone-400 "
                        style={{
                          background: `url(${p.url})`,
                          backgroundPosition: "center",
                        }}
                      ></div>
                    ) : (
                      <div className="w-full h-20 rounded-md border border-stone-400 flex items-center justify-center text-xs text-center px-2">
                        Vista previa no disponible, pero el archivo se adjuntará correctamente.
                      </div>
                    )}
                    <div className="my-1 text-xs font-bold">
                      {/*<div>{p.name}</div> */}
                      Tamaño {Number(p.size).toFixed(2)} MB
                    </div>
                  </div>
                ))}
              </div>
            }
            <div className="font-semibold text-center text-sm opensans px-4">
              {!text
                ? <div>
                    {currentStatus === 'refused' ? 
                      <div>
                        Has rechazado la transferencia.
                        <div>
                          ¿Quieres enviar una imagen como comprobante?
                        </div>
                      </div>
                    :
                      <div>
                        <div className="">
                          <div className="bg-white font-bold px-4 rounded-md text-red-600 w-fit mx-auto">
                            Advertencia
                          </div>
                          <div>
                            Enviar comprobantes de pago falsos resultará en la pérdida de sus fondos y el bloqueo permanente de su cuenta.
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                : 
                text}
            </div>

          </div>

        </>      
      
      {localSuccess && 
        <div className="px-4 py-4">
          <div className="text-center text-xl px-4 flex items-center justify-center gap-2 mb-4">
            ¡ Genial !
            <FaCheckCircle size={22} className="text-green-admin-light" />
          </div>
          
          <div className="text-lg text-center ">
            Las imágenes se han cargado con éxito.
          </div>
        </div>
      
      }
      
      
      <ToastContainer
        position="bottom-center"
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {showAlertMaxFile && 
        <div>
          <DialogInfoGeneral 
            show={showAlertMaxFile} 
            closeDialog={ () => setShowAlertMaxFile(false)}
          > 
          <div className="text-center text-base px-8 py-12 font-medium">
            Has alcanzado el límite máximo de 3 imágenes. 
            <div>
              Si deseas subir más, elimina alguna de las imágenes actuales.
            </div>
          </div>
          </DialogInfoGeneral>
        </div>
      }
    </div>
  );
};

export default DepositUploadManyFiles;
